/** Tier 4's per-tick pass and its block of the state hash (M21, docs/TOWERS_V2.md "Tier 4"). */
import { Fnv1a64 } from "../../maths/fnv1a64";
import type { LevelSim } from "../levelSim";
import { tickBranchAuras } from "./auras";
import { tickMinions } from "./minions";
import { tickTowerClocks } from "./shots";
import { tickBranchStatuses } from "./statuses";

/** Marks the tier-4 block in the state hash (written only once a branch is chosen this run). */
const ASCEND_HASH_TAG = 0xa5c4;

/**
 * Once per wave tick, after the shells and before the towers fire (LevelSim.step), and only once a branch is chosen:
 * enemy statuses and zones, the towers' skill clocks, the live auras, the phoenixes and spiders.
 */
export function tickBranches(sim: LevelSim): void {
  sim.shot.periodic = true;                // what follows is damage over time, not a shot (no Burrower dives on it)
  tickBranchStatuses(sim);
  tickTowerClocks(sim);
  tickBranchAuras(sim);
  tickMinions(sim);
  sim.shot.periodic = false;
}

/**
 * The tier-4 state (choices, skills, the special card's pace, per-tower clocks, enemy statuses, zones, phoenixes,
 * spiders). Written only once a branch is chosen, so every run without one — every golden recorded before tier 4 —
 * keeps its hash.
 */
export function writeAscensionHash(sim: LevelSim, h: Fnv1a64): void {
  const asc = sim.ascension;
  if (!asc.active) return;
  h.writeInt(ASCEND_HASH_TAG);
  for (const b of asc.branchOf) h.writeInt(b);
  for (const l of asc.learned) h.writeBool(l);
  h.writeInt(asc.offersSinceSpecial); h.writeInt(asc.specialsDealt);
  for (const t of sim.towerSlots) {
    if (!t.alive) continue;
    h.writeInt(t.shotCount); h.writeInt(t.skillTimer); h.writeInt(t.skillActive); h.writeInt(t.charge); h.writeInt(t.burstLeft); h.writeInt(t.zoneTimer);
    h.writeRaw(t.auraHaste.raw); h.writeRaw(t.rallyHaste.raw); h.writeRaw(t.auraRangeBonus.raw);
    h.writeInt(t.extraBeams.length); for (const id of t.extraBeams) h.writeInt(id);
  }
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    h.writeInt(e.stunTicksLeft); h.writeRaw(e.dotPerTick.raw); h.writeInt(e.dotTicksLeft); h.writeInt(e.dotSourceId);
    h.writeInt(e.sheepTicksLeft); h.writeRaw(e.auraSlow.raw); h.writeInt(e.frostbiteTicks); h.writeBool(e.ambushed);
  }
  h.writeInt(sim.nextZoneId);
  for (const z of sim.zones) { h.writeInt(z.id); h.writeInt(z.towerId); h.writeInt(z.kind); h.writeRaw(z.x.raw); h.writeRaw(z.y.raw); h.writeRaw(z.radius.raw); h.writeRaw(z.damagePerTick.raw); h.writeRaw(z.slow.raw); h.writeInt(z.ticksLeft); }
  for (const p of sim.phoenixes) { h.writeInt(p.towerId); h.writeBool(p.alive); h.writeRaw(p.x.raw); h.writeRaw(p.y.raw); h.writeInt(p.orbit); h.writeInt(p.lifeLeft); h.writeInt(p.respawnLeft); h.writeInt(p.novaLeft); }
  h.writeInt(sim.nextSpiderId);
  for (const s of sim.spiders) { h.writeInt(s.id); h.writeInt(s.towerId); h.writeInt(s.enemyId); h.writeInt(s.ticksLeft); h.writeInt(s.cooldown); }
}
